import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, Trash2, UserPlus, LogOut } from "lucide-react";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const Face = ({ u, size = "w-7 h-7" }) => (
  <span className={`${size} rounded-full overflow-hidden bg-[#1C2536] grid place-items-center text-[11px] font-semibold shrink-0 ring-2 ring-[#131A26]`} title={u.name}>
    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : u.name[0]}
  </span>
);

const DateBox = ({ label, value, onChange, testId }) => (
  <label className="flex-1 text-[11px] text-slate-400">
    {label}
    <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 font-mono text-xs [color-scheme:dark]" data-testid={testId} />
  </label>
);

export const PlanDialog = ({ country, P, children, index }) => {
  const plan = P.planFor(country);
  const { data: friends = [] } = useQuery({ queryKey: ["friends"], queryFn: api.friends });
  const [text, setText] = useState("");
  const invitable = friends.filter((f) => f.active && !plan?.members.some((m) => m.user_id === f.friend_id));

  const send = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    P.addNote(plan, text);
    setText("");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`plan-dialog-${index}`}>
        <DialogHeader>
          <p className="eyebrow !text-sky-300/80">Shared trip</p>
          <DialogTitle className="font-display text-2xl">{country}</DialogTitle>
          <DialogDescription>Everyone on the trip can set the dates and add notes.</DialogDescription>
        </DialogHeader>

        {plan && (
          <>
            <div className="flex flex-wrap gap-2" data-testid="plan-members">
              {plan.members.map((m) => (
                <span key={m.user_id} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-white/10 text-sm">
                  <Face u={m} /> {m.user_id === P.me ? "You" : m.name.split(" ")[0]}
                  {m.status === "pending" && <span className="text-[10px] font-mono text-amber-300">invited</span>}
                </span>
              ))}
            </div>
            <div className="flex gap-3">
              <DateBox label="From" value={plan.start_date} onChange={(v) => P.patch(plan, { start_date: v })} testId="plan-start-date" />
              <DateBox label="To" value={plan.end_date} onChange={(v) => P.patch(plan, { end_date: v })} testId="plan-end-date" />
            </div>
            <div>
              <p className="eyebrow !text-slate-500 mb-2">Notes</p>
              <ul className="space-y-2 max-h-56 overflow-y-auto" data-testid="plan-notes">
                {plan.notes.length === 0 && <li className="text-sm text-slate-500">No notes yet. Start planning.</li>}
                {plan.notes.map((n) => (
                  <li key={n.id} className="group flex gap-2" data-testid={`plan-note-${n.id}`}>
                    <Face u={n.author} size="w-6 h-6" />
                    <div className="flex-1 min-w-0 rounded-lg bg-white/[0.04] px-3 py-2">
                      <p className="text-[10px] font-mono text-slate-500">{n.author.user_id === P.me ? "You" : n.author.name.split(" ")[0]} · {formatDistanceToNowStrict(parseISO(n.at), { addSuffix: true })}</p>
                      <p className="text-sm whitespace-pre-line break-words">{n.text}</p>
                    </div>
                    {n.author.user_id === P.me && (
                      <button onClick={() => P.delNote(plan, n.id)} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition" aria-label="Delete note" data-testid={`plan-note-delete-${n.id}`}><Trash2 size={13} /></button>
                    )}
                  </li>
                ))}
              </ul>
              <form onSubmit={send} className="flex gap-2 mt-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ideas, flights, places to stay…" data-testid="plan-note-input" />
                <button type="submit" className="px-3 rounded-md bg-sky-400 text-[#06121c] hover:bg-sky-300 transition-colors" aria-label="Add note" data-testid="plan-note-submit"><Send size={14} /></button>
              </form>
            </div>
          </>
        )}

        <div>
          <p className="eyebrow !text-slate-500 mb-2">{plan ? "Invite more friends" : "Invite a friend you follow"}</p>
          {friends.length === 0 && <p className="text-sm text-slate-500" data-testid="plan-no-friends">Follow a friend in the Friends section first.</p>}
          {friends.length > 0 && invitable.length === 0 && <p className="text-sm text-slate-500">Everyone you follow is already on this trip.</p>}
          <div className="flex flex-wrap gap-2">
            {invitable.map((f) => (
              <button key={f.friend_id} onClick={() => P.invite(country, f)} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-sky-400/40 text-sm text-sky-200 hover:bg-sky-400/10 transition-colors" data-testid={`plan-invite-${f.friend_id}`}>
                <Face u={{ name: f.name, picture: f.picture }} /> <UserPlus size={13} /> {f.name.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        {plan && (
          <button onClick={() => P.leave(plan)} className="self-start flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400 transition-colors" data-testid="plan-leave-button"><LogOut size={12} /> Leave this trip</button>
        )}
      </DialogContent>
    </Dialog>
  );
};
